export interface ExtractedInvoiceData {
    currency: string;
    items: Array<{
        itemDescription: string;
        quantity: number;
        uom: string;
        unitPrice: number;
        total: number;
    }>;
    vendorCharges: Array<{ label: string; amount: number; chargeType?: string }>;
    taxes: Array<{ label: string; amount: number; rate?: number }>;
    itemsSubtotal: number;
    chargesSubtotal: number;
    taxTotal: number;
    grandTotal: number;
    extractionSource?: string;
}

export function ExtractedInvoicePreview({
    data,
    currency,
    chargesOnly = false,
}: {
    data: ExtractedInvoiceData;
    currency: string;
    chargesOnly?: boolean;
}) {
    const cur = data.currency || currency;
    const chargeTotal = (data.chargesSubtotal || 0) + (data.taxTotal || 0);

    return (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-4 text-sm">
            <p className="text-xs font-bold text-indigo-800 uppercase">
                Auto-read from PDF ({data.extractionSource || 'parsed'})
            </p>
            {chargesOnly && (
                <p className="text-xs text-indigo-700">
                    Product line items stay from the purchase order. Other charges on the invoice are imported below.
                </p>
            )}
            {!chargesOnly && data.items.length > 0 && (
                <div>
                    <p className="text-[10px] font-bold text-[hsl(var(--muted-foreground))] uppercase mb-1">Line items</p>
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[#0088cc]/65">
                                <th className="py-1">Item</th>
                                <th className="py-1">Qty</th>
                                <th className="py-1 text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((item, i) => (
                                <tr key={i} className="border-t border-indigo-100">
                                    <td className="py-1 pr-2">{item.itemDescription}</td>
                                    <td className="py-1">{item.quantity}</td>
                                    <td className="py-1 text-right font-semibold">
                                        {cur} {Number(item.total).toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {(data.vendorCharges?.length > 0 || data.taxes?.length > 0) && (
                <div className="space-y-1">
                    <p className="text-[10px] font-bold text-[hsl(var(--muted-foreground))] uppercase">Charges & taxes</p>
                    {data.vendorCharges.map((c, i) => (
                        <div key={`c-${i}`} className="flex justify-between">
                            <span>{c.label}</span>
                            <span className="font-semibold">{cur} {Number(c.amount).toFixed(2)}</span>
                        </div>
                    ))}
                    {data.taxes.map((t, i) => (
                        <div key={`t-${i}`} className="flex justify-between">
                            <span>{t.label}</span>
                            <span className="font-semibold">{cur} {Number(t.amount).toFixed(2)}</span>
                        </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-indigo-100 font-bold">
                        <span>Charges total</span>
                        <span>{cur} {Number(chargeTotal).toFixed(2)}</span>
                    </div>
                </div>
            )}
            <div className="flex justify-between pt-2 border-t border-indigo-200 font-bold text-base">
                <span>Grand total</span>
                <span>{cur} {Number(data.grandTotal).toFixed(2)}</span>
            </div>
        </div>
    );
}
